import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { BoardCard } from "./keyThemesBoardView.ts";

const FLIP_MS = 420;
const UPDATED_TAG_MS = 6_000;

/** Pure-themes board: 2-column grid of equal-height C1 folio cards.
 *  Signal hierarchy (weakest → strongest): FLIP slide only (repositioned,
 *  unchanged) → entrance + JUST EMERGED tag (new) → slide + flare + UPDATED
 *  tag (revision bump). All flags precomputed by deriveBoardView; this
 *  component measures, animates, and times the transient tag. */
export function KeyThemesBoardStage({
  cards,
  footerLabel,
}: {
  cards: BoardCard[];
  footerLabel: string | null;
}) {
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  const lastRects = useRef(new Map<string, DOMRect>());
  const [updatedTags, setUpdatedTags] = useState<ReadonlySet<string>>(new Set());
  const tagTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // FLIP: play each moved card's inverted delta from its previous rect.
  useLayoutEffect(() => {
    const previousRects = lastRects.current;
    const nextRects = new Map<string, DOMRect>();
    for (const [themeId, el] of cardRefs.current) {
      const rect = el.getBoundingClientRect();
      nextRects.set(themeId, rect);
      const previous = previousRects.get(themeId);
      if (!previous) continue;
      const dx = previous.left - rect.left;
      const dy = previous.top - rect.top;
      if (dx === 0 && dy === 0) continue;
      el.style.transition = "none";
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      void el.offsetWidth;
      el.style.transition = `transform ${FLIP_MS}ms ease`;
      el.style.transform = "";
    }
    lastRects.current = nextRects;
  }, [cards]);

  // Transient UPDATED tag: show for updated cards, self-clear after 6s.
  useEffect(() => {
    const updatedNow = cards.filter((card) => card.updated).map((card) => card.themeId);
    if (updatedNow.length === 0) return;
    setUpdatedTags((current) => new Set([...current, ...updatedNow]));
    for (const themeId of updatedNow) {
      const existing = tagTimers.current.get(themeId);
      if (existing) clearTimeout(existing);
      tagTimers.current.set(
        themeId,
        setTimeout(() => {
          tagTimers.current.delete(themeId);
          setUpdatedTags((current) => {
            const next = new Set(current);
            next.delete(themeId);
            return next;
          });
        }, UPDATED_TAG_MS),
      );
    }
  }, [cards]);
  useEffect(
    () => () => {
      for (const timer of tagTimers.current.values()) clearTimeout(timer);
    },
    [],
  );

  return (
    <main className="projector-ktboard" data-testid="projector-ktboard">
      <span className="projector-label ktb-label">Key themes</span>
      <div
        className={cards.length > 0 && cards.length <= 6 ? "ktb-grid ktb-sparse" : "ktb-grid"}
        data-testid="projector-ktboard-grid"
      >
        {cards.map((card) => (
          <div
            key={card.themeId}
            ref={(el) => {
              if (el) cardRefs.current.set(card.themeId, el);
              else cardRefs.current.delete(card.themeId);
            }}
            className={[
              "ktb-card",
              card.fresh ? "ktb-fresh" : "",
              card.entered ? "ktb-entered" : "",
              card.updated ? "ktb-updated" : "",
            ].filter(Boolean).join(" ")}
          >
            {card.fresh ? (
              <span className="ktb-tag">just emerged</span>
            ) : updatedTags.has(card.themeId) ? (
              <span className="ktb-tag">updated</span>
            ) : null}
            <h3 key={card.title} className={card.updated ? "ktb-text-in" : undefined}>
              {card.title}
            </h3>
            {card.summary && (
              <p key={card.summary} className={card.updated ? "ktb-text-in" : undefined}>
                {card.summary}
              </p>
            )}
          </div>
        ))}
        {cards.length === 0 && <div className="ktb-empty">Themes are being distilled…</div>}
      </div>
      <div className="ktb-foot">{footerLabel ? `last updated · ${footerLabel}` : ""}</div>
    </main>
  );
}
